import React, { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
  LayoutDashboard, FileSpreadsheet, Building2, PlusCircle, 
  LogOut, Menu, X, ChevronDown, User, UserCircle, Users, Shield, Settings, Database, Trash2,
  ShieldCheck, FileText, ChevronRight, ChevronsLeft, ChevronsRight, ClipboardList, Map as MapIcon, Route
} from 'lucide-react';

import NotificationBell from './NotificationBell';
import LogoHorizontal from './LogoHorizontal';
import ChangesPopup from './ChangesPopup';
import { RouteClosurePopup } from './RouteClosurePopup';
import SessionTimeoutPopup from './SessionTimeoutPopup';
import { useHelpMode } from '../contexts/HelpModeContext';
import { HelpCircle } from 'lucide-react';

const SIDEBAR_COLLAPSED_KEY = 'xenicos.sidebar.collapsed';
const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Layout = ({ children }) => {
  const directReview = useLocation().pathname.startsWith('/pendientes-revision/');
  const { user, logout } = useAuth();
  const { enabled: helpEnabled, setEnabled: setHelpEnabled } = useHelpMode();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Desktop-only: persist collapse preference in localStorage so it survives
  // navigation and reload. Default = COLAPSADO (true) — el usuario ve iconos
  // por defecto y expande manualmente al pulsar el botón de menú.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const v = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
      // Si no hay preferencia guardada, arrancamos colapsados.
      if (v === null) return true;
      return v === '1';
    } catch { return true; }
  });
  // Detecta si estamos en desktop (>= lg breakpoint = 1024px). El estado
  // `collapsed` sólo debe aplicar en desktop: en móvil el drawer siempre
  // se muestra expandido con etiquetas completas.
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia('(min-width: 1024px)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener ? mq.addEventListener('change', handler) : mq.addListener(handler);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', handler) : mq.removeListener(handler);
    };
  }, []);
  // `visualCollapsed` = valor efectivo para renderizar layout (móvil siempre expande).
  const visualCollapsed = collapsed && isDesktop;
  const [menuOpen, setMenuOpen] = useState(false);
  const [pendientesCount, setPendientesCount] = useState(0);
  const menuRef = useRef(null);
  const sidebarRef = useRef(null);
  const menuToggleRef = useRef(null);

  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0'); } catch {}
  }, [collapsed]);

  // Fetch the "Pendientes de revisión" badge count.
  useEffect(() => {
    if (!user) return;
    let active = true;
    const fetchCount = async () => {
      try {
        const r = await axios.get(`${API}/me/pendientes-revision/count`, { withCredentials: true });
        if (active) setPendientesCount(r.data?.count || 0);
      } catch { /* noop */ }
    };
    fetchCount();
    const i = setInterval(fetchCount, 30000);
    const onRefresh = () => fetchCount();
    window.addEventListener('xenicos:refresh-bell', onRefresh);
    window.addEventListener('xenicos:refresh-pendientes', onRefresh);
    return () => {
      active = false;
      clearInterval(i);
      window.removeEventListener('xenicos:refresh-bell', onRefresh);
      window.removeEventListener('xenicos:refresh-pendientes', onRefresh);
    };
  }, [user]);

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Auto-colapsar el sidebar desktop al hacer clic fuera. El botón menú
  // (toggle) tiene su propio manejador que expande/colapsa — lo excluimos
  // para no colapsar+expandir en el mismo evento.
  useEffect(() => {
    if (!isDesktop || collapsed) return;
    const handleClickOutside = (e) => {
      if (sidebarRef.current && sidebarRef.current.contains(e.target)) return;
      if (menuToggleRef.current && menuToggleRef.current.contains(e.target)) return;
      setCollapsed(true);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDesktop, collapsed]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const role = user?.role;
  const isSuperadmin = role === 'superadmin';
  const isInversor = role === 'inversor';
  const isMediador = role === 'mediador';
  // Roles v2 navigation:
  //   superadmin → todo (Dashboard, Nuevo, Activos, Contactos, Papelera, Auditoría, Usuarios, Parametros)
  //   admin      → operativo (Dashboard, Nuevo, Activos, Contactos) — no critical config
  //   gestor     → operativo igual que admin pero con scope limitado a asignados (filtrado en backend)
  //   mediador   → solo Dashboard + Activos + Contactos (sin Importar / Crear)
  //   inversor   → solo Base de Activos
  let navItems;
  if (isInversor) {
    navItems = [
      { to: '/activos', icon: Building2, label: 'Base de Activos' },
      { to: '/pendientes-revision', icon: ClipboardList, label: 'Pendientes de revisión', badge: pendientesCount },
    ];
  } else if (isMediador) {
    // Mediador: sin Dashboard (no necesita resumen global). Solo activos
    // asignados, contactos asociados, y su buzón de pendientes.
    navItems = [
      { to: '/activos', icon: Building2, label: 'Base de Activos' },
      { to: '/mapa', icon: MapIcon, label: 'Mapa' },
      { to: '/recorridos', icon: Route, label: 'Mis recorridos' },
      { to: '/pendientes-revision', icon: ClipboardList, label: 'Pendientes de revisión', badge: pendientesCount },
      { to: '/contactos', icon: Users, label: 'Contactos' },
    ];
  } else if (isSuperadmin) {
    navItems = [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { group: 'nuevo', icon: PlusCircle, label: 'Nuevo Activo', children: [
        { to: '/crear-activo', icon: PlusCircle, label: 'Crear Activo' },
        { to: '/importar', icon: FileSpreadsheet, label: 'Importar Excel' },
        { to: '/importar-pdf', icon: FileText, label: 'Importar Ficha PDF' },
      ]},
      { to: '/activos', icon: Building2, label: 'Base de Activos' },
      { to: '/mapa', icon: MapIcon, label: 'Mapa' },
      { to: '/recorridos', icon: Route, label: 'Mis recorridos' },
      { to: '/pendientes-revision', icon: ClipboardList, label: 'Pendientes de revisión', badge: pendientesCount },
      { to: '/contactos', icon: Users, label: 'Contactos' },
      { to: '/papelera', icon: Trash2, label: 'Papelera' },
      { to: '/auditoria', icon: ShieldCheck, label: 'Auditoría' },
      { to: '/usuarios', icon: Shield, label: 'Usuarios' },
      { to: '/parametros', icon: Settings, label: 'Parametros' },
    ];
  } else {
    // admin (v2) y gestor — misma navegación operativa.
    // El scope de activos lo aplica el backend automáticamente.
    navItems = [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { group: 'nuevo', icon: PlusCircle, label: 'Nuevo Activo', children: [
        { to: '/crear-activo', icon: PlusCircle, label: 'Crear Activo' },
        { to: '/importar', icon: FileSpreadsheet, label: 'Importar Excel' },
        { to: '/importar-pdf', icon: FileText, label: 'Importar Ficha PDF' },
      ]},
      { to: '/activos', icon: Building2, label: 'Base de Activos' },
      { to: '/mapa', icon: MapIcon, label: 'Mapa' },
      { to: '/recorridos', icon: Route, label: 'Mis recorridos' },
      { to: '/pendientes-revision', icon: ClipboardList, label: 'Pendientes de revisión', badge: pendientesCount },
      { to: '/contactos', icon: Users, label: 'Contactos' },
    ];
  }
  if (role === 'admin') navItems.push({ to: '/parametros?tab=servicer', icon: Settings, label: 'Parámetros' });
  const avatarUrl = user?.avatar_url
    ? `${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}`
    : null;

  const AvatarCircle = ({ size = 32 }) => (
    <div
      className="rounded-full flex items-center justify-center overflow-hidden flex-shrink-0"
      style={{
        width: size, height: size,
        background: 'rgba(34, 211, 238, 0.15)',
        border: '1px solid var(--color-border)',
      }}
    >
      {avatarUrl ? (
        <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
      ) : (
        <User className="w-4 h-4" style={{ color: 'var(--color-blue-accent)' }} />
      )}
    </div>
  );

  const location = typeof window !== 'undefined' ? window.location.pathname : '';
  const [nuevoOpen, setNuevoOpen] = useState(['/crear-activo', '/importar', '/importar-pdf'].includes(location));

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--color-bg-primary)' }} data-testid="app-layout">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        ref={sidebarRef}
        className={`fixed lg:static inset-y-0 left-0 z-50 transform transition-all duration-200 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } ${visualCollapsed ? 'w-16 sidebar-collapsed' : 'w-64'}`}
        style={{
          background: 'var(--color-bg-secondary)',
          borderRight: '1px solid var(--color-border)',
        }}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`flex items-center h-20 ${visualCollapsed ? 'justify-center px-2' : 'justify-between px-4'}`} style={{ borderBottom: '1px solid var(--color-border)' }}>
            {!visualCollapsed && (
              <Link to="/dashboard" aria-label="XÉNICOS — ir al dashboard" className="flex items-center" style={{ maxWidth: 'calc(100% - 40px)' }}>
                <LogoHorizontal height={48} style={{ maxWidth: '100%' }} />
              </Link>
            )}
            {visualCollapsed && (
              <Link to="/dashboard" aria-label="XÉNICOS — ir al dashboard" className="flex items-center justify-center w-9 h-9 rounded-md" style={{ background: 'rgba(34, 211, 238, 0.12)', border: '1px solid var(--color-border)' }}>
                <span className="text-sm font-bold tracking-tight" style={{ color: 'var(--color-blue-accent)' }}>X</span>
              </Link>
            )}
            <button 
              className="lg:hidden p-2 rounded-lg hover:bg-white/5"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1" style={{ overflow: 'visible' }}>
            {navItems.map((item) => (
              item.group ? (
                <div key={item.group}>
                  <button
                    onClick={() => {
                      // When collapsed, expanding the group via click would
                      // overflow the icon column → uncollapse first.
                      if (visualCollapsed) { setCollapsed(false); setNuevoOpen(true); }
                      else setNuevoOpen(v => !v);
                    }}
                    className="sidebar-item w-full justify-between"
                    data-tooltip={item.label}
                    data-testid={`nav-group-${item.group}`}
                  >
                    <div className="flex items-center gap-3">
                      <item.icon className="w-5 h-5" />
                      <span className="sidebar-label">{item.label}</span>
                    </div>
                    <ChevronRight className={`w-4 h-4 transition-transform sidebar-chevron ${nuevoOpen ? 'rotate-90' : ''}`} />
                  </button>
                  {nuevoOpen && !visualCollapsed && (
                    <div className="ml-4 mt-1 space-y-1 border-l-2 border-slate-100 pl-2">
                      {item.children.map(child => (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          onClick={() => setSidebarOpen(false)}
                          className={({ isActive }) => `sidebar-item text-sm ${isActive ? 'active' : ''}`}
                          data-testid={`nav-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <child.icon className="w-4 h-4" />
                          <span className="sidebar-label">{child.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `sidebar-item ${isActive ? 'active' : ''}`
                  }
                  data-tooltip={item.badge ? `${item.label} (${item.badge})` : item.label}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="sidebar-label">{item.label}</span>
                  {item.badge > 0 && (
                    <span
                      className="ml-auto sidebar-label inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full text-[10px] font-bold"
                      style={{ background: 'rgba(239, 68, 68, 0.18)', color: '#fca5a5' }}
                      data-testid={`nav-badge-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </NavLink>
              )
            ))}
          </nav>

          {/* Collapse toggle (desktop only) */}
          <div className="hidden lg:block px-3 py-2" style={{ borderTop: '1px solid var(--color-border)' }}>
            <button
              onClick={() => setCollapsed(c => !c)}
              className="sidebar-item w-full"
              data-tooltip={visualCollapsed ? 'Expandir menú' : 'Colapsar menú'}
              title={visualCollapsed ? 'Expandir menú' : 'Colapsar menú'}
              data-testid="sidebar-collapse-toggle"
            >
              {visualCollapsed
                ? <ChevronsRight className="w-5 h-5" />
                : <ChevronsLeft className="w-5 h-5" />
              }
              <span className="sidebar-label">{visualCollapsed ? 'Expandir' : 'Colapsar'}</span>
            </button>
          </div>

          {/* User section */}
          <div className={`${visualCollapsed ? 'p-2' : 'p-4'}`} style={{ borderTop: '1px solid var(--color-border)' }}>
            <Link
              to="/perfil"
              className={`flex items-center rounded-lg hover:bg-white/5 transition-colors ${visualCollapsed ? 'justify-center p-1' : 'gap-3 px-2 py-2'}`}
              title={visualCollapsed ? `${user?.name || 'Usuario'} · ${user?.email || ''}` : undefined}
              data-testid="sidebar-user-link"
            >
              <AvatarCircle size={visualCollapsed ? 36 : 32} />
              {!visualCollapsed && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>{user?.name || 'Usuario'}</p>
                  <p className="text-xs truncate" style={{ color: 'var(--color-text-muted)' }}>{user?.email}</p>
                </div>
              )}
            </Link>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen min-w-0">
        {/* Top header */}
        <header
          className="h-16 flex items-center justify-between px-6"
          style={{
            background: 'var(--color-bg-secondary)',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          {/* Botón menú: en móvil abre el drawer, en desktop colapsa/expande el sidebar. */}
          <button
            ref={menuToggleRef}
            className="p-2 rounded-lg hover:bg-white/5 transition-colors"
            onClick={() => {
              if (isDesktop) setCollapsed(c => !c);
              else setSidebarOpen(true);
            }}
            title={visualCollapsed ? 'Expandir menú' : 'Colapsar menú'}
            aria-label={visualCollapsed ? 'Expandir menú' : 'Colapsar menú'}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" style={{ color: 'var(--color-text-secondary)' }} />
          </button>

          <div className="flex-1" />

          {/* (Feb 2026) Toggle "Modo ayuda" — persistido en `users.help_mode_enabled`.
              Sólo se muestra si hay usuario autenticado. */}
          <button
            type="button"
            onClick={() => setHelpEnabled(!helpEnabled)}
            className={`hidden sm:flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-colors mr-1 ${
              helpEnabled
                ? 'bg-blue-500/15 text-blue-400 hover:bg-blue-500/25'
                : 'text-slate-400 hover:bg-white/5'
            }`}
            title={helpEnabled
              ? 'Modo ayuda ACTIVADO — click para desactivar'
              : 'Modo ayuda DESACTIVADO — click para activar'}
            data-testid="toggle-help-mode"
            aria-pressed={helpEnabled}
          >
            <HelpCircle className="w-4 h-4" />
            <span>Ayuda</span>
            <span
              className={`inline-block w-6 h-3.5 rounded-full relative transition-colors ${helpEnabled ? 'bg-blue-500' : 'bg-slate-600'}`}
              aria-hidden="true"
            >
              <span
                className={`absolute top-0.5 w-2.5 h-2.5 rounded-full bg-white transition-all ${helpEnabled ? 'left-3' : 'left-0.5'}`}
              />
            </span>
          </button>

          <NotificationBell />

          <div className="relative" ref={menuRef}>
            <button
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors"
              onClick={() => setMenuOpen(!menuOpen)}
              data-testid="user-menu-btn"
            >
              <AvatarCircle size={32} />
              <span className="hidden sm:inline text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>{user?.name}</span>
              <ChevronDown className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
            </button>
            {menuOpen && (
              <div
                className="absolute right-0 mt-2 w-52 rounded-lg shadow-xl z-50 py-1"
                style={{
                  background: 'var(--color-bg-secondary)',
                  border: '1px solid var(--color-border-strong)',
                }}
              >
                <Link
                  to="/perfil"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 w-full px-4 py-2 text-sm transition-colors"
                  style={{ color: 'var(--color-text-secondary)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(34, 211, 238, 0.08)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  data-testid="profile-link"
                >
                  <UserCircle className="w-4 h-4" />
                  Mi perfil
                </Link>
                <div style={{ height: 1, background: 'var(--color-border)', margin: '4px 0' }} />
                <button
                  onClick={() => { setMenuOpen(false); handleLogout(); }}
                  className="flex items-center gap-2 w-full px-4 py-2 text-sm transition-colors"
                  style={{ color: '#FCA5A5' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.10)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                  Cerrar sesión
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6 md:p-10 overflow-auto" style={{ background: 'var(--color-bg-primary)' }}>
          {children}
        </main>
      </div>

      {/* Popups globales (solo se renderizan si hay sesión activa) */}
      {!directReview && <ChangesPopup />}
      {!directReview && <RouteClosurePopup />}
      <SessionTimeoutPopup />
    </div>
  );
};

export default Layout;
